import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  CircularProgress,
  Grid,
  TextField,
  Button,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon 
} from '@mui/icons-material';
import axios from 'axios';
import baseURL from '../../../baseURL';
import MasterPageShell, {
  masterPaperSx,
  masterListPaperSx,
  masterSectionTitleSx,
  masterPrimaryBtnSx,
  masterFieldSx,
  masterTableHeadCellSx,
} from '../../common/MasterPageShell';

const GroupCodeListPage = () => {
  const [groupCodes, setGroupCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [groupCodePage, setGroupCodePage] = useState(0);
  const [groupCodeRowsPerPage, setGroupCodeRowsPerPage] = useState(50);
  const [groupCodeTotalDocs, setGroupCodeTotalDocs] = useState(0);

  // Create/Edit Group Code State
  const [groupCodeForm, setGroupCodeForm] = useState({
    _id: '',
    name: '',
    code: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(false);
  const [createError, setCreateError] = useState(null);

  // Delete Group Code State
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupCodeToDelete, setGroupCodeToDelete] = useState(null);

  const fetchGroupCodesPage = async (page = 0, rowsPerPage = 50) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseURL}/group-codes`, {
        params: { page: page + 1, limit: rowsPerPage },
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const res = response.data;
      setGroupCodes(res.data || []);
      setGroupCodeTotalDocs(res.totalDocs ?? (res.data || []).length);
      setGroupCodePage(page);
      setGroupCodeRowsPerPage(rowsPerPage);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch group codes');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGroupCodesPage(0, groupCodeRowsPerPage);
  }, []);

  const handleGroupCodePageChange = (event, newPage) => {
    fetchGroupCodesPage(newPage, groupCodeRowsPerPage);
  };

  const handleGroupCodeRowsPerPageChange = (event) => {
    const newRows = parseInt(event.target.value, 10);
    setGroupCodeRowsPerPage(newRows);
    fetchGroupCodesPage(0, newRows);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setGroupCodeForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateGroupCode = async (e) => {
    e.preventDefault();
    setCreateLoading(true);
    setCreateError(null);

    try {
      const token = localStorage.getItem('token');
      let response;

      if (isEditing) {
        response = await axios.put(`${baseURL}/group-codes/update/${groupCodeForm._id}`, groupCodeForm, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      } else {
        response = await axios.post(`${baseURL}/group-codes/create`, groupCodeForm, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
      }

      setGroupCodeForm({ _id: '', name: '', code: '' });
      setIsEditing(false);
      setCreateSuccess(true);
      setCreateLoading(false);
      fetchGroupCodesPage(groupCodePage, groupCodeRowsPerPage);
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to create/update group code');
      setCreateLoading(false);
    }
  };

  const handleEditGroupCode = (groupCode) => {
    setGroupCodeForm({
      _id: groupCode._id,
      name: groupCode.name,
      code: groupCode.code
    });
    setIsEditing(true);
  };

  const handleDeleteGroupCode = async () => {
    if (!groupCodeToDelete) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${baseURL}/group-codes/delete/${groupCodeToDelete._id}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setDeleteDialogOpen(false);
      setGroupCodeToDelete(null);
      setCreateSuccess(true);
      fetchGroupCodesPage(groupCodePage, groupCodeRowsPerPage);
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to delete group code');
    }
  };

  const handleCloseSnackbar = () => {
    setCreateSuccess(false);
    setCreateError(null);
  };

  if (loading) {
    return (
      <MasterPageShell title="Group Codes" subtitle="Master · Accounts" loading />
    );
  }
  if (error) {
    return (
      <MasterPageShell title="Group Codes" subtitle="Master · Accounts">
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </MasterPageShell>
    );
  }

  return (
    <MasterPageShell title="Group Codes" subtitle="Master · Accounts">
      <>
        <Paper elevation={0} sx={{ ...masterPaperSx, mb: 2, height: 'auto' }}>
          <Typography sx={masterSectionTitleSx}>Group code ranges</Typography>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary"><strong>001–300:</strong> Income (credit)</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary"><strong>301–600:</strong> Expenses (debit)</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary"><strong>601–800:</strong> Liabilities (credit)</Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" color="text.secondary"><strong>801–900:</strong> Assets (debit)</Typography>
            </Grid>
          </Grid>
        </Paper>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Paper elevation={0} sx={masterPaperSx}>
              <Typography sx={masterSectionTitleSx}>
                {isEditing ? 'Edit group code' : 'Create group code'}
              </Typography>
              <Box component="form" onSubmit={handleCreateGroupCode} noValidate>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="name"
                  label="Group code name"
                  name="name"
                  value={groupCodeForm.name}
                  onChange={handleInputChange}
                  sx={masterFieldSx}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="code"
                  label="Group code (3-digit)"
                  name="code"
                  value={groupCodeForm.code}
                  onChange={handleInputChange}
                  helperText="001–300 Income · 301–600 Expenses · 601–800 Liabilities · 801–900 Assets"
                  inputProps={{ maxLength: 3 }}
                  sx={masterFieldSx}
                />
                <Button type="submit" fullWidth variant="contained" disabled={createLoading} sx={masterPrimaryBtnSx}>
                  {isEditing ? 'Update group code' : 'Create group code'}
                </Button>
                {isEditing && (
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setIsEditing(false);
                      setGroupCodeForm({ _id: '', name: '', code: '' });
                    }}
                    sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 650 }}
                  >
                    Cancel
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Group Codes List */}
          <Grid item xs={12} md={8}>
            <Paper elevation={0} sx={masterListPaperSx}>
              <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                <Typography sx={{ ...masterSectionTitleSx, mb: 0 }}>Group codes list</Typography>
              </Box>
              <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={masterTableHeadCellSx}>Name</TableCell>
                    <TableCell sx={masterTableHeadCellSx}>Code</TableCell>
                    <TableCell align="right" sx={masterTableHeadCellSx}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupCodes.map((groupCode) => (
                    <TableRow key={groupCode._id} hover>
                      <TableCell sx={{ fontWeight: 650 }}>{groupCode.name}</TableCell>
                      <TableCell>{groupCode.code}</TableCell>
                      <TableCell align="right">
                        <IconButton 
                          color="primary"
                          size="small"
                          onClick={() => handleEditGroupCode(groupCode)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          color="error"
                          size="small"
                          onClick={() => {
                            setGroupCodeToDelete(groupCode);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={groupCodeTotalDocs}
                page={groupCodePage}
                onPageChange={handleGroupCodePageChange}
                rowsPerPage={groupCodeRowsPerPage}
                onRowsPerPageChange={handleGroupCodeRowsPerPageChange}
                rowsPerPageOptions={[25, 50, 100, 200]}
                labelRowsPerPage="Rows per page:"
              />
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
        >
          <DialogTitle>Delete Group Code</DialogTitle>
          <DialogContent>
            Are you sure you want to delete this group code?
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button 
              color="error" 
              onClick={handleDeleteGroupCode}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Success/Error Snackbars */}
        <Snackbar 
          open={createSuccess} 
          autoHideDuration={3000} 
          onClose={handleCloseSnackbar}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity="success" 
            sx={{ width: '100%' }}
          >
            Operation successful!
          </Alert>
        </Snackbar>

        <Snackbar 
          open={!!createError} 
          autoHideDuration={3000} 
          onClose={handleCloseSnackbar}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity="error" 
            sx={{ width: '100%' }}
          >
            {createError}
          </Alert>
        </Snackbar>
      </>
    </MasterPageShell>
  );
};

export default GroupCodeListPage;